import React, { useState, useEffect, Suspense, useContext } from "react";
import {
	HashRouter as Router,
	Route,
	Routes,
	useNavigate,
	useLocation,
} from "react-router-dom";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Backdrop from "@mui/material/Backdrop";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { handleLogout } from "./utils/logoutUtils";
import { getToken } from "./utils/authUtils";
import { GlobalContext } from "./context/GlobalContext";
import LanguageWrapper from "./LanguageWrapper";
import AuthGuard from "./components/AuthGuard";
import ChunkLoadErrorBoundary from "./components/ChunkLoadErrorBoundary";
import "./styles/global.scss";
import useUserDatas from "./hooks/useUserDatas";
import useFetchStandards from "./hooks/useFetchStandards";
import useBellNotification from "./hooks/useBellNotification";
import useNotification from "./hooks/useNotification";

// 懶加載頁面
const LoginPage = React.lazy(() => import("./login_page/LoginPage"));
const ExternalRedirect = React.lazy(() => import("./components/ExternalRedirect"));
const ResetPasswordPage = React.lazy(() =>
	import("./reset_password_page/ResetPasswordPage")
);
const ResetPasswordSuccessPage = React.lazy(() =>
	import("./reset_password_page/ResetPasswordSuccessPage")
);
const MapPage = React.lazy(() => import("./map/MapPage"));
const Plant = React.lazy(() => import("./plant_area/PlantV2"));
const AlarmManagement = React.lazy(() =>
	import("./alarm_management/alarmManagement")
);
const Event = React.lazy(() => import("./alarm_management/event/Event"));
const CarbonEmissionMonitor = React.lazy(() =>
	import("./carbon_emission_monitor/CarbonMonitorV2")
);
const DxReport = React.lazy(() => import("./dx_report/DxReportV2"));
const Tables = React.lazy(() => import("./tables/Tables"));
const NotificationManagement = React.lazy(() =>
	import("./admin/NotificationManagement")
);

function App() {
	const { t, i18n } = useTranslation();
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [openSnackbar, setOpenSnackbar] = useState(false);
	const [openBackdrop, setOpenBackdrop] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();

	const pathSegments = location.pathname.split("/");
	const pathLang = pathSegments[1] || "";

	const isPublicRoute = (() => {
		const base = `/${pathLang}`;
		const p = location.pathname;

		return (
			p === base ||
			p === `${base}/login-page` ||
			p === `${base}/landing-page` ||
			p === `${base}/product-catalog` ||
			p === `${base}/user-manual` ||
			p.startsWith(`${base}/reset-password-page`) ||
			p === `${base}/reset-password-success-page`
		);
	})();

	const lang = pathLang;
	const { isIntranetUsed } = useContext(GlobalContext);
	const userDatas = useSelector((state) => state.userDatas);

	const { refreshUserDatas } = useUserDatas({
		t,
		lang,
		setSnackbarMessage,
		setOpenSnackbar,
		setOpenBackdrop,
		navigate,
		handleLogout,
		skipInitialFetch: isPublicRoute,
	});

	useEffect(() => {
		// 當路徑不是 `/event` 時清除 eventId
		// console.log("isPublicRoute=", isPublicRoute);
		if (!isPublicRoute) {
			refreshUserDatas();
			if (!location.pathname.includes("/event")) {
				Cookies.remove("id");
			}
		}
	}, [location.pathname, isPublicRoute]);

	useEffect(() => {
		const suspendStatus = userDatas?.suspend;
		if (suspendStatus) {
			handleLogout({
				t,
				lang,
				setSnackbarMessage,
				setOpenSnackbar,
				setOpenBackdrop,
				navigate,
				logoutMessage: t("mapPage.accountSuspended"),
			});
		}
	}, [userDatas?.suspend]);

	// 登入後取得使用者 access flags（遮蔽/隱藏控制）
	useEffect(() => {
		if (isPublicRoute || !userDatas?._id) return;
		const token = getToken();
		if (!token) return;
		fetch(`${process.env.REACT_APP_API_URL}/devices/user-access-flags`, {
			headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
		})
			.then((res) => res.ok ? res.json() : null)
			.then((flags) => {
				if (flags) sessionStorage.setItem("accessFlags", JSON.stringify(flags));
			})
			.catch((err) => console.warn("[accessFlags] Failed to fetch:", err?.message));
	}, [userDatas?._id, isPublicRoute]);

	useBellNotification();
	useNotification();
	useFetchStandards();

	return (
		<div className="App">
			<LanguageWrapper>
				<AuthGuard
					setSnackbarMessage={setSnackbarMessage}
					setOpenSnackbar={setOpenSnackbar}
					setOpenBackdrop={setOpenBackdrop}
				>
					<ChunkLoadErrorBoundary resetKey={location.pathname}>
						{/* 懸停效果，用於處理懶加載時的加載動畫或佔位符 */}
						<Suspense fallback={<div>Loading...</div>}>
						<Routes>
						{/* 首頁/登入頁面 */}
						<Route path="/:lng" element={<LoginPage />} />
						{/* 登入頁面 */}
						<Route path="/:lng/login-page" element={<LoginPage />} />
						{/* 著陸頁面 */}
						<Route path="/:lng/landing-page" element={<ExternalRedirect url="https://chiuchau.com/air-chiuchau-com" />} />
						{Boolean(isIntranetUsed) === false && (
							<>
								{/* 地圖頁面 */}
								<Route
									path="/:lng/map-page"
									element={
										<MapPage
											t={t}
											lang={lang}
											navigate={navigate}
											setOpenBackdrop={setOpenBackdrop}
											setOpenSnackbar={setOpenSnackbar}
											setSnackbarMessage={setSnackbarMessage}
											handleLogout={handleLogout}
											userDatas={userDatas}
										/>
									}
								/>
							</>
						)}
						{/* 警報管理頁面 */}
						<Route
							path="/:lng/alarm-management"
							element={
								<AlarmManagement
									t={t}
									lang={lang}
									navigate={navigate}
									setOpenBackdrop={setOpenBackdrop}
									setOpenSnackbar={setOpenSnackbar}
									setSnackbarMessage={setSnackbarMessage}
									handleLogout={handleLogout}
								/>
							}
						/>
						<Route
							path="/:lng/event"
							element={
								<Event
									t={t}
									lang={lang}
									navigate={navigate}
									setOpenBackdrop={setOpenBackdrop}
									setOpenSnackbar={setOpenSnackbar}
									setSnackbarMessage={setSnackbarMessage}
								/>
							}
						/>
						{/* 廠區主頁面 */}
						<Route
							path="/:lng/plant/:plant"
							element={
								<Plant
									t={t}
									lang={lang}
									navigate={navigate}
									setOpenBackdrop={setOpenBackdrop}
									setOpenSnackbar={setOpenSnackbar}
									setSnackbarMessage={setSnackbarMessage}
									handleLogout={handleLogout}
									userDatas={userDatas}
								/>
							}
						/>
						{/* 碳排監控頁面 */}
						<Route
							path="/:lng/plant/:plant/carbon-emission-monitor"
							element={
								<CarbonEmissionMonitor
									t={t}
									lang={lang}
									navigate={navigate}
									setOpenBackdrop={setOpenBackdrop}
									setOpenSnackbar={setOpenSnackbar}
									setSnackbarMessage={setSnackbarMessage}
									handleLogout={handleLogout}
								/>
							}
						/>
						{/* 診斷報告頁面 */}
						<Route
							path="/:lng/plant/:plant/dx-report"
							element={
								<DxReport
									t={t}
									lang={lang}
									navigate={navigate}
									setOpenBackdrop={setOpenBackdrop}
									setOpenSnackbar={setOpenSnackbar}
									setSnackbarMessage={setSnackbarMessage}
									handleLogout={handleLogout}
								/>
							}
						/>
						{/* 表格相關頁面，包含多個子模塊 */}
						<Route
							path="/:lng/plant/:plant/modelId/:modelId/serialNumber/:serialNumber/tables/*"
							element={
								<Tables
									t={t}
									i18n={i18n}
									lang={lang}
									navigate={navigate}
									location={location}
									setOpenBackdrop={setOpenBackdrop}
									setOpenSnackbar={setOpenSnackbar}
									setSnackbarMessage={setSnackbarMessage}
									handleLogout={handleLogout}
									userDatas={userDatas}
								/>
							}
						/>
						{/* Admin 通知管理頁面 */}
						<Route
							path="/:lng/admin/notifications"
							element={
								<NotificationManagement
									t={t}
									lang={lang}
									navigate={navigate}
									setOpenBackdrop={setOpenBackdrop}
									setOpenSnackbar={setOpenSnackbar}
									setSnackbarMessage={setSnackbarMessage}
									handleLogout={handleLogout}
								/>
							}
						/>
						{/* 重設密碼頁面 */}
						<Route
							path="/:lng/reset-password-page/:token"
							element={<ResetPasswordPage />}
						/>
						<Route
							path="/:lng/reset-password-success-page"
							element={<ResetPasswordSuccessPage />}
						/>
						</Routes>
						</Suspense>
					</ChunkLoadErrorBoundary>
				</AuthGuard>
			</LanguageWrapper>
			<Backdrop open={openBackdrop} style={{ zIndex: 9999, color: "#fff" }}>
				<Snackbar
					open={openSnackbar}
					anchorOrigin={{ vertical: "top", horizontal: "center" }}
				>
					<MuiAlert severity="error">{snackbarMessage}</MuiAlert>
				</Snackbar>
			</Backdrop>
		</div>
	);
}

export default function AppWrapper() {
	return (
		<Router future={{ v7_startTransition: true }}>
			<App />
		</Router>
	);
}
